import { useMutation, useQueryClient } from "@tanstack/react-query";
import { configuration } from "src/configuration";
import type { EditFormSubmissionDto, FormAnswerValueDto } from "src/http";
import { FetchError, queryKeys, useAccessToken } from "src/http";

type Variables = {
  formSubmissionGuid: string;
  recipientUserId: string;
  dateRangeStartUtc: string | null;
  dateRangeEndUtc: string | null;
  videoUrl: string | null;
  answers: {
    questionGuid: string;
    value: FormAnswerValueDto | null;
  }[];
  referencedActionItemGuids: string[];
  referencedTaskItemGuids: string[];
  submitted: boolean;
};

const useEditFormSubmission = () => {
  const { getAccessToken } = useAccessToken();
  const queryClient = useQueryClient();

  async function editFormSubmissionRequest(variables: Variables) {
    const accessToken = await getAccessToken();
    const body: EditFormSubmissionDto = {
      recipientUserId: variables.recipientUserId,
      dateRangeStartUtc: variables.dateRangeStartUtc,
      dateRangeEndUtc: variables.dateRangeEndUtc,
      videoUrl: variables.videoUrl,
      answers: variables.answers,
      referencedActionItemGuids: variables.referencedActionItemGuids,
      referencedTaskItemGuids: variables.referencedTaskItemGuids,
      submitted: variables.submitted,
    };

    const response = await fetch(`${configuration.apiRootUrl}/form-submissions/${variables.formSubmissionGuid}`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "put",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return useMutation({
    mutationFn: editFormSubmissionRequest,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries(queryKeys.formSubmissions.getFormSubmissions());
      queryClient.invalidateQueries(queryKeys.formSubmissions.getFormSubmission(variables.formSubmissionGuid));
    },
  });
};

export { useEditFormSubmission };
